import { safeNextPath } from '@hoopoe/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Copy, Download } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { AuthLayout, FormError } from '../components/auth/auth-layout';
import { QrCode } from '../components/auth/qr-code';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';
import { sessionQuery } from '../lib/session';

type Step =
  | { s: 'password' }
  | { s: 'forgot'; email: string }
  | { s: 'forgot-sent'; email: string }
  | { s: 'email' }
  | { s: 'code'; email: string }
  | { s: 'enrol'; email: string }
  | { s: 'totp'; email: string }
  | { s: 'backup'; codes: string[] };

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>) => ({
    next: safeNextPath(s.next),
  }),
  // Resume a half-finished sign-in after a reload; skip the page when already signed in.
  loader: async ({ context }) => context.queryClient.fetchQuery({ ...sessionQuery, staleTime: 0 }),
  component: Login,
});

const TITLES: Record<Step['s'], string> = {
  password: 'Sign in',
  forgot: 'Set or reset your password',
  'forgot-sent': 'Check your email',
  email: 'Sign in with a code',
  code: 'Check your email',
  enrol: 'Set up your authenticator',
  totp: 'Enter your authenticator code',
  backup: 'Save your backup codes',
};

function Login() {
  const session = Route.useLoaderData();
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>(() =>
    session.status === 'needs-totp' ? { s: session.next, email: session.email } : { s: 'password' },
  );
  // Whether this environment still offers a code by email (D393: staging and production sign in with a password).
  const methods = useQuery({
    queryKey: ['auth-methods'],
    queryFn: () => api<{ password: boolean; emailCode: boolean }>('/auth/methods'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const emailCode = methods.data?.emailCode ?? false;
  const signedIn = async (n: 'enrol' | 'totp' | 'done', email: string) => {
    // `done`: this environment signs in without the authenticator (staging, D389).
    if (n === 'done') await finish();
    else setStep({ s: n, email });
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session.status === 'signed-in') navigate({ to: next ?? '/' });
  }, [session, next, navigate]);

  const finish = async () => {
    // Drop the cached "needs authenticator" state so the route guard fetches the new session.
    qc.removeQueries({ queryKey: sessionQuery.queryKey });
    await navigate({ to: next ?? '/' });
  };

  const run = (fn: () => Promise<void>) => async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      if (err instanceof ApiError && err.code === 'too_many_attempts') setStep({ s: 'password' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={TITLES[step.s]}>
      {step.s === 'password' && (
        <PasswordStep
          busy={busy}
          error={error}
          emailCode={emailCode}
          onSubmit={(email, password) =>
            run(async () => {
              const { next: n } = await api<{ next: 'enrol' | 'totp' | 'done' }>('/auth/login', { body: { email, password } });
              await signedIn(n, email);
            })()
          }
          onForgot={email => {
            setError(null);
            setStep({ s: 'forgot', email });
          }}
          onEmailCode={() => {
            setError(null);
            setStep({ s: 'email' });
          }}
        />
      )}
      {step.s === 'forgot' && (
        <ForgotStep
          initialEmail={step.email}
          busy={busy}
          error={error}
          onSubmit={email =>
            run(async () => {
              await api('/auth/password/request', { body: { email } });
              setStep({ s: 'forgot-sent', email });
            })()
          }
          onBack={() => {
            setError(null);
            setStep({ s: 'password' });
          }}
        />
      )}
      {step.s === 'forgot-sent' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground" role="status">
            If <strong className="text-foreground">{step.email}</strong> has access, we've sent it a link to choose a password. The link
            works once and expires in 1 hour.
          </p>
          <button type="button" className="text-sm underline" onClick={() => setStep({ s: 'password' })}>
            Back to sign in
          </button>
        </div>
      )}
      {step.s === 'email' && (
        <EmailStep
          busy={busy}
          error={error}
          onSubmit={email =>
            run(async () => {
              await api('/auth/request-code', { body: { email } });
              setStep({ s: 'code', email });
            })()
          }
        />
      )}
      {step.s === 'code' && (
        <CodeStep
          email={step.email}
          busy={busy}
          error={error}
          onSubmit={code =>
            run(async () => {
              const { next: n } = await api<{ next: 'enrol' | 'totp' | 'done' }>('/auth/verify-code', {
                body: { email: step.email, code },
              });
              await signedIn(n, step.email);
            })()
          }
          onResend={() =>
            run(async () => {
              await api('/auth/request-code', { body: { email: step.email } });
            })()
          }
          onBack={() => setStep({ s: 'email' })}
        />
      )}
      {step.s === 'email' && (
        <button type="button" className="mt-4 text-sm underline" onClick={() => setStep({ s: 'password' })}>
          Sign in with your password instead
        </button>
      )}
      {step.s === 'enrol' && (
        <EnrolStep
          email={step.email}
          busy={busy}
          error={error}
          onConfirm={code =>
            run(async () => {
              const { backupCodes } = await api<{ backupCodes: string[] }>('/auth/totp/enrol/confirm', { body: { code } });
              await api('/onboarding/checklist/authenticator', { body: {} }).catch(() => {});
              setStep({ s: 'backup', codes: backupCodes });
            })()
          }
        />
      )}
      {step.s === 'totp' && (
        <TotpStep
          busy={busy}
          error={error}
          onSubmit={code =>
            run(async () => {
              await api('/auth/totp/verify', { body: { code } });
              await finish();
            })()
          }
        />
      )}
      {step.s === 'backup' && <BackupCodes codes={step.codes} onDone={finish} />}
    </AuthLayout>
  );
}

function PasswordStep({
  busy,
  error,
  emailCode,
  onSubmit,
  onForgot,
  onEmailCode,
}: {
  busy: boolean;
  error: string | null;
  emailCode: boolean;
  onSubmit: (email: string, password: string) => void;
  onForgot: (email: string) => void;
  onEmailCode: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(email.trim(), password);
      }}
    >
      <p className="text-sm text-muted-foreground">Hoopoe is invite-only. Sign in with your email address and password.</p>
      <div className="space-y-1.5">
        <label htmlFor="email" className="text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@organisation.org"
        />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="password" className="text-sm font-medium">
          Password
        </label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
      </div>
      <FormError message={error} />
      <Button type="submit" className="w-full" disabled={busy}>
        Sign in
      </Button>
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <button type="button" className="underline" onClick={() => onForgot(email.trim())}>
          Forgot your password?
        </button>
        {emailCode && (
          <button type="button" className="underline" onClick={onEmailCode}>
            Email me a code instead
          </button>
        )}
      </div>
      <p className="text-center text-sm text-muted-foreground">
        No access yet?{' '}
        <Link to="/request-access" className="font-medium text-foreground underline">
          Request access
        </Link>
      </p>
    </form>
  );
}

function ForgotStep({
  initialEmail,
  busy,
  error,
  onSubmit,
  onBack,
}: {
  initialEmail: string;
  busy: boolean;
  error: string | null;
  onSubmit: (email: string) => void;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(email.trim());
      }}
    >
      <p className="text-sm text-muted-foreground">
        Never set a password, or forgot it? Enter your email address and we'll send you a link to choose a new one.
      </p>
      <div className="space-y-1.5">
        <label htmlFor="forgot-email" className="text-sm font-medium">
          Email
        </label>
        <Input
          id="forgot-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@organisation.org"
        />
      </div>
      <FormError message={error} />
      <Button type="submit" className="w-full" disabled={busy}>
        Email me a link
      </Button>
      <button type="button" className="text-sm underline" onClick={onBack}>
        Back to sign in
      </button>
    </form>
  );
}

function EmailStep({ busy, error, onSubmit }: { busy: boolean; error: string | null; onSubmit: (email: string) => void }) {
  const [email, setEmail] = useState('');
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(email.trim());
      }}
    >
      <p className="text-sm text-muted-foreground">Hoopoe is invite-only. We'll email you a one-time code.</p>
      <div className="space-y-1.5">
        <label htmlFor="email" className="text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@organisation.org"
        />
      </div>
      <FormError message={error} />
      <Button type="submit" className="w-full" disabled={busy}>
        Email me a code
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        No access yet?{' '}
        <Link to="/request-access" className="font-medium text-foreground underline">
          Request access
        </Link>
      </p>
    </form>
  );
}

function CodeStep({
  email,
  busy,
  error,
  onSubmit,
  onResend,
  onBack,
}: {
  email: string;
  busy: boolean;
  error: string | null;
  onSubmit: (c: string) => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(code);
      }}
    >
      <p className="text-sm text-muted-foreground">
        If <strong className="text-foreground">{email}</strong> has access, a 6-digit code is on its way. It expires in 10 minutes.
      </p>
      <div className="space-y-1.5">
        <label htmlFor="code" className="text-sm font-medium">
          Code from the email
        </label>
        <Input
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          required
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          className="tabular tracking-widest"
        />
      </div>
      <FormError message={error} />
      <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
        Continue
      </Button>
      <div className="flex justify-between text-sm">
        <button type="button" onClick={onBack} className="underline">
          Use another email
        </button>
        <button type="button" onClick={onResend} className="underline" disabled={busy}>
          Send a new code
        </button>
      </div>
    </form>
  );
}

function EnrolStep({
  email,
  busy,
  error,
  onConfirm,
}: {
  email: string;
  busy: boolean;
  error: string | null;
  onConfirm: (c: string) => void;
}) {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    api<{ secret: string; uri: string }>('/auth/totp/enrol/start', { body: {} })
      .then(setSetup)
      .catch(e => setLoadError(e instanceof ApiError ? e.message : 'Could not start setup. Reload the page.'));
  }, []);
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        onConfirm(code);
      }}
    >
      <p className="text-sm text-muted-foreground">
        Scan this with an authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit code it shows. You'll use it
        every time you sign in as <strong className="text-foreground">{email}</strong>.
      </p>
      <FormError message={loadError} />
      {setup && (
        <div className="flex flex-col items-center gap-2">
          <QrCode value={setup.uri} label="QR code for your authenticator app" />
          <details className="w-full text-sm">
            <summary className="cursor-pointer text-muted-foreground">Can't scan? Enter the key by hand</summary>
            <code data-testid="totp-secret" className="mt-2 block rounded bg-muted p-2 text-center font-mono text-xs break-all">
              {setup.secret.match(/.{1,4}/g)?.join(' ')}
            </code>
          </details>
        </div>
      )}
      <div className="space-y-1.5">
        <label htmlFor="totp" className="text-sm font-medium">
          Code from your app
        </label>
        <Input
          id="totp"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          className="tabular tracking-widest"
        />
      </div>
      <FormError message={error} />
      <Button type="submit" className="w-full" disabled={busy || !setup || code.length !== 6}>
        Confirm
      </Button>
    </form>
  );
}

function TotpStep({ busy, error, onSubmit }: { busy: boolean; error: string | null; onSubmit: (c: string) => void }) {
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  return (
    <form
      className="space-y-4"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(code);
      }}
    >
      <div className="space-y-1.5">
        <label htmlFor="totp" className="text-sm font-medium">
          {backup ? 'Backup code' : '6-digit code from your authenticator app'}
        </label>
        <Input
          id="totp"
          inputMode={backup ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          required
          value={code}
          onChange={e => setCode(backup ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={backup ? 'xxxx-xxxx-xx' : undefined}
          className="tabular tracking-widest"
        />
      </div>
      <FormError message={error} />
      <Button type="submit" className="w-full" disabled={busy || (!backup && code.length !== 6)}>
        Sign in
      </Button>
      <p className="text-sm text-muted-foreground">
        {backup ? (
          <button
            type="button"
            className="underline"
            onClick={() => {
              setBackup(false);
              setCode('');
            }}
          >
            Use the authenticator app instead
          </button>
        ) : (
          <>
            Lost your phone?{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                setBackup(true);
                setCode('');
              }}
            >
              Use a backup code
            </button>{' '}
            or ask an admin to reset your authenticator.
          </>
        )}
      </p>
    </form>
  );
}

export function BackupCodes({
  codes,
  onDone,
  doneLabel = 'Continue to Hoopoe',
}: {
  codes: string[];
  onDone: () => void;
  doneLabel?: string;
}) {
  const [saved, setSaved] = useState(false);
  const text = `Hoopoe backup codes (each works once)\n\n${codes.join('\n')}\n`;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        If you lose your phone, each of these codes signs you in once. Store them somewhere safe, like a password manager. They won't be
        shown again.
      </p>
      <ol aria-label="Backup codes" className="grid grid-cols-2 gap-1 rounded bg-muted p-3 font-mono text-sm tabular">
        {codes.map(c => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => navigator.clipboard?.writeText(text)}>
          <Copy /> Copy
        </Button>
        <Button type="button" variant="outline" size="sm" asChild>
          <a href={`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`} download="hoopoe-backup-codes.txt">
            <Download /> Download
          </a>
        </Button>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} /> I've saved my backup codes
      </label>
      <Button className="w-full" disabled={!saved} onClick={onDone}>
        {doneLabel}
      </Button>
    </div>
  );
}
